// Pure, side-effect-free geometry helpers for the editable cable takeoff.
//
// Everything the editable-takeoff store derives lives here so the store (and the
// 4 edit UIs) can stay thin. All coordinates are in PDF-point space (the same
// space the panel renders in). A circuit's length is measured by circuitMetres,
// which counts a run shared by several devices once.

import type {
  CircuitPathResult,
  CircuitPathsState,
  CoordinateExtents,
  RouteWaypoint,
} from "../../ReviewPage/hooks/useCircuitCablePaths/types";
import { deviceKeyOf, readDeviceEdit } from "./deviceKey";
import { resolveCircuitEdits } from "./circuitEdits";
import type {
  CableSourceMarker,
  CableTakeoffGeometry,
  CableTraySegment,
} from "../utils/fetchCableTakeoffPaths";
import { circuitMetres } from "./circuitMetres";
import { liveAddedCircuits, mintRunColour } from "./addedCircuits";
import type { AddedCableCircuit } from "./addedCircuits";
import { fingerprintCircuit } from "./routedBilling";
import { governedByFeedAreas, isInsideFeedArea, normaliseFeedArea } from "./feedAreas";
import type { CableSystem } from "./cableSystems";
import type { FeedArea, StoredFeedArea } from "./feedAreas";
import type { RoutedBilling } from "./routedBilling";
import { resolveMainBoard } from "./mainBoard";
import type { MainBoardAnswer, MainBoardState } from "./mainBoard";
import { resolveCircuitBoard } from "./circuitBoards";
import type { BoardRoleAnswer, CircuitBoard } from "./circuitBoards";
import { routeInputKey } from "./routeCache";
import { routeDestinations } from "./routeDestinations";
import { feedsDevice, pickFeeder, resolveBoardFeeds } from "./boardFeeds";
import type { BoardFeed, PickFeederReason, ResolvedBoardFeed } from "./boardFeeds";
import { CATEGORY_ORDER, categoryOfCableSystem } from "./deviceCategories";
import type { DeviceCategory, ElementCategory } from "./deviceCategories";
import { mintCircuitId } from "./addedCircuits";
import type { RouteAnswer, RouteCache, RouteRequest } from "./routeCache";
import { resolveHeights } from "./installationHeights";
import type { StoredHeights } from "./installationHeights";
import { installationLevelOf } from "./installationLevelOf";
import { buildDeviceHeightProfile, circuitVerticalMetres, heightStopsOf } from "./heightProfile";
import type { CircuitVertical, DeviceHeightProfile } from "./heightProfile";
import { circuitLegs } from "./circuitLegs";
import type { CircuitLegMetres, LegKind } from "./circuitLegs";
import { switchingRoleOf } from "./switchingRoleOf";
import {
  FEEDER_RGB,
  feederCircuitId,
  feederDestinationId,
  feederElementId,
} from "./feederCircuits";
import type { FeederCircuit } from "./feederCircuits";
import { POWER_BOARD_CATEGORIES } from "./deviceCategories";

export interface RoutingInputs {
  /** Router answers keyed by their exact inputs. */
  cache: RouteCache;
  /** Once the boards are settled every run is the router's, not just the ones this code guessed. */
  routeAll?: boolean;
  /** The trays the router reads; a cable's height follows the same set. */
  trays?: CableTraySegment[];
}

const NO_ROUTING: RoutingInputs = { cache: {} };

/** Bumped when stored documents need a migration; v2 added the main board. */
export const EDIT_SCHEMA_VERSION = 2;

/** Agent default scale — annotation pixels per metre. */
export const DEFAULT_PX_PER_M = 118;

/** Editable overrides applied on top of the agent's base geometry. */
/** One removal action, and everything it struck. */
export interface RemovalGroup {
  id: string;
  destinationIds: string[];
  /**
   * Set once Karl has asked about this group. An OPEN group still collects
   * single removals, so striking twelve devices off a row one at a time is one
   * question rather than twelve; a closed one can only ever be joined by a new
   * group, or an answered question would silently grow.
   */
  closed?: boolean;
}

export interface CableEditOverrides extends StoredHeights {
  schemaVersion?: number;
  /** Which board is the main one on this plan, keyed by marker label. */
  mainBoard?: MainBoardAnswer;
  /** Per board: does it feed its own circuits, or is it fed via the main board. */
  boardRoleAnswers?: Record<string, BoardRoleAnswer>;
  /** Which device categories each board feeds, by marker label; absent boards use the suggestion. */
  boardFeeds?: Record<string, BoardFeed>;
  /** Categories left out of this takeoff altogether ("nicht in dieser Kalkulation"). */
  unbilledCategories?: DeviceCategory[];
  /** His own names for circuits, by circuit id; they win over printed and generated ones. */
  circuitNames?: Record<string, string>;
  /** His own identifiers for circuits ("A32" for "L1"), by circuit id. */
  circuitKeys?: Record<string, string>;
  /** Moved source board positions (PDF space), keyed by board marker label. */
  sourceOverrides?: Record<string, RouteWaypoint>;
  /** Replaced destination waypoints, keyed by destinationId. */
  routeOverrides?: Record<string, RouteWaypoint[]>;
  /**
   * Cable type the estimator chose, keyed by circuitId. Display and BoQ only —
   * a different spec does not move a single waypoint, so no metre changes.
   */
  cableTypeByCircuit?: Record<string, string>;
  /** A leg of a run on a type of its own — the run between two Wechselschalter — keyed by circuitId. */
  cableTypeByLeg?: Record<string, Partial<Record<LegKind, string>>>;
  /** Cable types he created for this plan, so a type exists before any run carries it. */
  cableTypes?: string[];
  /**
   * Feed point the estimator moved a run to, keyed by circuitId, valued by the
   * board's marker label. This one DOES change the metres: the run is
   * re-anchored to the new board, so it must go through the same re-anchoring
   * the source move uses and never a second implementation.
   */
  feedPointByCircuit?: Record<string, string>;
  /**
   * Areas of the plan fed from one board, oldest first. A RULE rather than an
   * edit: it is re-applied on every derive, so a run that lands inside one
   * later feeds from the same board without him drawing it again.
   *
   * It is the WEAKER of the two feed-point answers. `feedPointByCircuit` names
   * ONE run and this names a region, so the run he pointed by hand wins — the
   * more specific instruction, which is the same order the whole pipeline uses.
   */
  areaFeeds?: FeedArea[];
  /** Karl's board step is settled; the circuit walk stays locked until it is. */
  boardsConfirmed?: boolean;
  /** Boards he said are fed from another floor: they get no feeder on this plan. */
  feederOffFloor?: Record<string, true>;
  /** Karl's heights step is settled; the circuit walk stays locked until it is. */
  heightsConfirmed?: boolean;
  /** Karl's tray step is settled; the circuit walk waits for it too. */
  trayHeightsConfirmed?: boolean;
  /** Karl's walls step is settled: the detected walls are what he wants the router to respect. */
  wallsConfirmed?: boolean;
  /** Boards whose circuits he has checked in Karl's circuit step, by marker label. */
  circuitBoardsChecked?: string[];
  /**
   * What the router last answered for a circuit, keyed by circuitId.
   *
   * This is the ONLY place the no-tray surcharge can come from. `circuitMetres`
   * measures the polyline and nothing else, so a run that rides no tray reads
   * ~23% under the run's own figure at a 30% Zuschlag — and the headline flips
   * onto our number the moment there is any edit, so the whole gap lands in one
   * jump with an edit to blame it on. Applying the surcharge needs `on_tray_m`,
   * which needs the router; so the router's answer is stored, not recomputed.
   *
   * A stale entry is still USED. It is wrong only by the delta of his edit,
   * where measuring afresh is wrong by the whole surcharge, every time.
   */
  routedByCircuit?: Record<string, RoutedBilling>;
  /**
   * The tray network as it stood at the last plan-wide recalculate.
   *
   * Stored so a tray drawn, moved or renamed afterwards is VISIBLE as a reason
   * to ask again. Welding is plan-global and not length-neutral, so one new
   * edge can change which candidate network wins for runs nowhere near it —
   * which is why this is a plan-level fact and not a per-circuit one.
   */
  /**
   * What the router last read about the BUILDING — the trays and the walls — at
   * the moment the plan was recalculated. Its name predates the walls; it is
   * kept because documents in the field are stamped under it, and an absent
   * stamp reads as "never recalculated", which is the safe default.
   */
  routedTrayFingerprint?: string;
  /** Device (destination) reassigned to another circuit, keyed by destinationId. */
  groupOverrides?: Record<string, string>;
  /**
   * A device the run placed on NO circuit, adopted onto one by the estimator.
   *
   * Keyed on the run's `element_id`, NOT a destinationId: it has no destination
   * until this override mints one, and `element_id` is what a correction is
   * ultimately persisted under. Every other device override re-parents a path
   * that already exists; this is the only one that creates one.
   */
  adoptedDevices?: Record<string, AdoptedDevice>;
  /** Devices (destinations) removed from the takeoff entirely, keyed by destinationId. */
  removedDevices?: Record<string, true>;
  /**
   * The removals, in the order he made them — one entry per action, so a box
   * that struck twelve devices stays one thing he can look at and put back.
   * `removedDevices` remains the flat truth `applyOverrides` reads; this is the
   * record of HOW it got that way.
   */
  removalGroups?: RemovalGroup[];
  /** Next removal number. Ids must survive restoring an earlier group. */
  removalSeq?: number;
  /** Sources hidden from the plan, keyed by board marker label. */
  removedSources?: Record<string, true>;
  /** Renamed sources (display name), keyed by board marker label. */
  sourceNames?: Record<string, string>;
  /** Feed points the estimator added that the run never found, keyed by label. */
  addedSources?: Record<string, AddedCableSource>;
  /**
   * Runs the estimator created that the estimation never published, keyed by
   * the circuit id minted for them.
   *
   * The only override that brings a CIRCUIT into existence. Every other
   * circuit-keyed map describes one the run already has — which is why a way
   * the schematic lists and the run caught nothing on had no row, no id and
   * nowhere to put a device. Same shape as `addedSources`, and it stands down
   * the moment the estimation publishes that id itself (`liveAddedCircuits`).
   */
  addedCircuits?: Record<string, AddedCableCircuit>;
  /**
   * Lengths he DREW that no device's cable runs through, keyed by circuitId.
   *
   * `routeOverrides` stores one polyline per destination, so a length nothing
   * routes over has nowhere to live in it — and it was thrown away the moment
   * he left the mode: *"i clicked and made doted new route, but its not saved
   * even after done, why?"*. This is the whole of what cannot be expressed
   * there, and nothing else: every length a cable DOES run over is already in
   * that destination's polyline, so storing the graph as well would be two
   * sources of truth for the same edges, free to disagree.
   *
   * Rebuilding is exact: the polylines reproduce every used edge, these are
   * exactly the unused ones, and their union is the graph he left.
   */
  strandedByCircuit?: Record<string, StrandedEdge[]>;
}

/**
 * One drawn length with no cable on it, in PDF points.
 *
 * It is NOT measured. `circuitMetres` counts the segments of the paths, which
 * are the cable that feeds devices; a length feeding nothing bills nothing. The
 * number he signs must not quietly grow by a stroke he may have half-finished —
 * and the dashed encoding on the plan says exactly that, in no total.
 */
export interface StrandedEdge {
  a: RouteWaypoint;
  b: RouteWaypoint;
}

/** A board the run missed entirely, placed by hand. Position is PDF space. */
export interface AddedCableSource {
  name: string;
  x: number;
  y: number;
}

/** Recover the destination index encoded in a `${circuitId}-${destIndex}` id. */
export function parseDestIndex(destinationId: string): number {
  const m = destinationId.match(/-(\d+)$/);
  return m ? Number(m[1]) : 0;
}

/**
 * The same point, to within a float's worth of drift. Never a proximity test:
 * two boards are hundreds of points apart and a cable's own start is compared
 * against them exactly, because the run's base geometry is free to start some
 * distance from the marker `boardLabelForPaths` picked for it.
 */
function samePoint(a: RouteWaypoint, b: RouteWaypoint): boolean {
  return Math.abs(a.x - b.x) < 0.01 && Math.abs(a.y - b.y) < 0.01;
}

/**
 * Re-anchor a route so it starts at a (possibly moved) source.
 *
 * It replaced waypoint 0 and kept the rest, which drew a raw DIAGONAL from the
 * new board to wherever the old second waypoint happened to be — a device moved
 * onto a board across the building got a cable straight across the plan: "i am
 * adding new route it traverse all the map". Cable is not pulled diagonally
 * through a building and a diagonal does not measure like one, so the approach
 * is stepped: along x, then along y, which is what the name always claimed.
 */
export function manhattanReanchor(
  source: RouteWaypoint,
  waypoints: RouteWaypoint[],
): RouteWaypoint[] {
  if (waypoints.length === 0) return [{ ...source }];
  const rest = waypoints.slice(1);
  const next = rest[0];
  const start = { x: source.x, y: source.y };
  if (!next) return [start];
  // Already orthogonal: a corner on top of the run would be a node that does
  // nothing, and every extra node is one more thing he can grab by mistake.
  if (next.x === start.x || next.y === start.y) return [start, ...rest];
  return [start, { x: next.x, y: start.y }, ...rest];
}

/** Closest point on segment ab to p, and how far that is. */
function closestOnSegment(
  p: RouteWaypoint,
  a: RouteWaypoint,
  b: RouteWaypoint,
): { point: RouteWaypoint; d2: number } {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const len2 = abx * abx + aby * aby;
  const t =
    len2 === 0
      ? 0
      : Math.max(
          0,
          Math.min(1, ((p.x - a.x) * abx + (p.y - a.y) * aby) / len2),
        );
  const point = { x: a.x + t * abx, y: a.y + t * aby };
  const dx = p.x - point.x;
  const dy = p.y - point.y;
  return { point, d2: dx * dx + dy * dy };
}

/**
 * Where a new device taps INTO a run that is already drawn — the nearest point
 * on any of its cables. A device added to a run used to get its own cable all
 * the way back to the board, so the plan filled with parallel runs to the same
 * place and the metres counted a trunk per device instead of one.
 */
export function nearestPointOnPaths(
  paths: CircuitPathResult[],
  point: RouteWaypoint,
): RouteWaypoint | null {
  let best: RouteWaypoint | null = null;
  let bestD2 = Infinity;
  for (const path of paths) {
    for (let i = 1; i < path.waypoints.length; i += 1) {
      const hit = closestOnSegment(
        point,
        path.waypoints[i - 1],
        path.waypoints[i],
      );
      if (hit.d2 >= bestD2) continue;
      bestD2 = hit.d2;
      best = hit.point;
    }
    // A single-waypoint path is a board with nothing drawn off it yet.
    if (path.waypoints.length === 1) {
      const only = path.waypoints[0];
      const dx = point.x - only.x;
      const dy = point.y - only.y;
      const d2 = dx * dx + dy * dy;
      if (d2 < bestD2) {
        bestD2 = d2;
        best = { x: only.x, y: only.y };
      }
    }
  }
  return best;
}

/** The spur from a tap point to a device: along x, then along y. */
export function manhattanSpur(
  from: RouteWaypoint,
  to: RouteWaypoint,
): RouteWaypoint[] {
  const start = { x: from.x, y: from.y };
  const end = { x: to.x, y: to.y };
  if (start.x === end.x || start.y === end.y) return [start, end];
  return [start, { x: end.x, y: start.y }, end];
}

function extentsOf(waypoints: RouteWaypoint[]): CoordinateExtents {
  const xs = waypoints.map((w) => w.x);
  const ys = waypoints.map((w) => w.y);
  return {
    minX: Math.min(...xs),
    minY: Math.min(...ys),
    maxX: Math.max(...xs),
    maxY: Math.max(...ys),
  };
}

function clonePath(p: CircuitPathResult): CircuitPathResult {
  return { ...p, waypoints: p.waypoints.map((w) => ({ ...w })) };
}

/** Nearest source marker label for a circuit, by its first waypoint (base space). */
function boardLabelForPaths(
  paths: CircuitPathResult[],
  sources: CableSourceMarker[],
): string | null {
  const first = paths[0]?.waypoints[0];
  if (!first || sources.length === 0) return null;
  let best: string | null = null;
  let bestD = Infinity;
  for (const s of sources) {
    const d = (s.x - first.x) ** 2 + (s.y - first.y) ** 2;
    if (d < bestD) {
      bestD = d;
      best = s.label;
    }
  }
  return best;
}

/** Last-waypoint marker for a removed device, in BASE (PDF-point) space. */
export interface RemovedDeviceMarker {
  x: number;
  y: number;
  destinationId: string;
  /** What a removal group remembers him striking — `deviceKeyOf`, so it
   * survives a re-run that renumbers every destination. */
  deviceKey: string;
  circuitId: string;
}

export interface DerivedCableTakeoff {
  geometry: CableTakeoffGeometry;
  /**
   * The estimator's feed point and cable type per circuit, re-addressed onto
   * the circuits in THIS geometry. The panel reads these rather than the raw
   * document: an edit stored under a circuit id the run has since renamed is
   * still his edit, and the row has to mark it as one.
   */
  feedPointByCircuit: Record<string, string>;
  cableTypeByCircuit: Record<string, string>;
  /** Metres the cable spends going up and down, per circuit. */
  verticalByCircuit: Record<string, CircuitVertical>;
  /** Where each device's cable rises and drops, and why. */
  heightProfilesByCircuit: Record<string, DeviceHeightProfile[]>;
  /** The run's metres split by leg, so a leg on another cable type is billed as that type. */
  legsByCircuit: Record<string, CircuitLegMetres>;
  /** His cable type per leg, re-addressed onto the circuits in this geometry. */
  cableTypeByLegByCircuit: Record<string, Partial<Record<LegKind, string>>>;
  /** Effective per-circuit total = billed + vertical. */
  lengthsByCircuit: Record<string, number>;
  /** Routed length per circuit (geometry × scale), before the vertical metres. */
  measuredByCircuit: Record<string, number>;
  /**
   * What each circuit BILLS horizontally: the router's answer where it
   * has one, `measured` where it does not. They differ by the no-tray
   * surcharge, which is per off-tray metre and so cannot be measured here.
   */
  billedByCircuit: Record<string, number>;
  /**
   * Circuits whose stored billed figure no longer describes the drawing,
   * because he has edited the run since it was routed. The figure is still
   * used — it is wrong only by his edit, where measuring afresh is wrong by the
   * whole surcharge — and the card marks the run so he can ask again.
   */
  staleBilledCircuits: string[];
  /**
   * Runs the router should be asked about again — the union of a stale billed
   * figure and geometry this code invented (a tap spur, which is not
   * tray-aware and can cut across a room).
   *
   * One mark on screen for two reasons, because the estimator's question is the
   * same either way: does this cable still describe what is drawn?
   */
  circuitsNeedingRoute: string[];
  /**
   * Holes through drawn walls per circuit, as the ROUTER answered.
   *
   * Not derivable here and deliberately not derived: whether a run goes round a
   * wall or breaks through it is the router's decision, taken against the walls
   * in Postgres, and this is only the count it came back with.
   */
  wallCrossingsByCircuit: Record<string, number>;
  totalM: number;
  /** Positions (base/PDF space) of removed devices, for plan highlighting. */
  removedDeviceMarkers: RemovedDeviceMarker[];
  /**
   * Devices left unfed because the board feeding them was removed. They are
   * still on the drawing and still have to end up on some circuit, so this is
   * a question for the estimator rather than a quiet subtraction.
   */
  orphanedDevices: OrphanedDevices;
  /**
   * Which board actually feeds each circuit — the estimator's feed point where
   * he set one, else the run's nearest-marker guess. Published because the
   * source list counts what a board carries, and striking a board has to know
   * which circuits leave with it.
   */
  feedingBoardByCircuit: Record<string, string | null>;
  /** The same answer with the reason it was chosen, for provenance labels. */
  circuitBoardByCircuit: Record<string, CircuitBoard>;
  mainBoard: MainBoardState;
  /** Circuits whose cable is still the app's guess, and the request that would replace it. */
  routeRequests: RouteRequest[];
  /** The cables that feed a board, by circuit id. */
  feederCircuits: Record<string, FeederCircuit>;
  /** Circuits the app made to give a board the devices of its categories, by id. */
  appCircuits: Record<string, AppCircuit>;
  /** The category most of a circuit's devices belong to; null when none is known. */
  categoryByCircuit: Record<string, DeviceCategory | null>;
  /** What each live board feeds, and whether he said so. */
  feedsByBoard: Record<string, ResolvedBoardFeed>;
  /** Devices of a category no board on this plan feeds. */
  unfedCategories: Partial<Record<DeviceCategory, string[]>>;
  /** Live devices per board and category, with their device types, for the feeds checklist. */
  deviceCountsByBoard: Record<string, Partial<Record<DeviceCategory, Record<string, number>>>>;
  /** Devices no board reaches: left without cable until he gives them one. */
  unfedDevices: UnfedDevice[];
}

export interface UnfedDevice {
  elementId: string;
  destinationId: string;
  name: string;
  category: DeviceCategory;
  position: RouteWaypoint;
}

export interface AppCircuit {
  sourceLabel: string;
  category: DeviceCategory;
}

export const APP_CIRCUIT_KEY = "app";

export const appCircuitId = (sourceLabel: string, category: DeviceCategory): string =>
  mintCircuitId({ boardName: sourceLabel, key: `${APP_CIRCUIT_KEY}·${category}` });

export interface OrphanedDevices {
  circuitCount: number;
  deviceCount: number;
  /** The removed boards that caused it, by label. */
  sourceLabels: string[];
  /** Circuits left feeding from nowhere, so the list can show them as struck. */
  circuitIds: string[];
}

interface CountDevicesByBoardArgs {
  circuitPaths: Record<string, CircuitPathResult[]>;
  feedingBoardByCircuit: Record<string, string | null>;
  categoryOfPath: (path: CircuitPathResult, circuitId: string) => ElementCategory;
  typeNameOf: (path: CircuitPathResult) => string | undefined;
}

const countDevicesByBoard = ({
  circuitPaths,
  feedingBoardByCircuit,
  categoryOfPath,
  typeNameOf,
}: CountDevicesByBoardArgs): DerivedCableTakeoff["deviceCountsByBoard"] => {
  const counts: DerivedCableTakeoff["deviceCountsByBoard"] = {};
  Object.entries(circuitPaths).forEach(([cid, paths]) => {
    const board = feedingBoardByCircuit[cid];
    if (!board) return;
    paths.forEach((path) => {
      const category = categoryOfPath(path, cid);
      if (category === "other") return;
      const byCategory = (counts[board] ??= {});
      const byType = (byCategory[category] ??= {});
      const type = typeNameOf(path) ?? "";
      byType[type] = (byType[type] ?? 0) + 1;
    });
  });
  return counts;
};

/**
 * Apply all overrides to the base geometry and derive per-circuit lengths.
 * Pure: never mutates `base`.
 */
export function applyOverrides(
  base: CableTakeoffGeometry,
  overrides: CableEditOverrides,
  imageScaleFactor: number,
  scalePxPerM: number,
  /** Which installation each circuit belongs to; only power runs fall under a zone. */
  systemByCircuit: Record<string, CableSystem | undefined> = {},
  routing: RoutingInputs = NO_ROUTING,
): DerivedCableTakeoff {
  const sourceOv = overrides.sourceOverrides ?? {};
  const routeOv = overrides.routeOverrides ?? {};
  const groupOv = overrides.groupOverrides ?? {};
  const removedOv = overrides.removedSources ?? {};
  const removedDevOv = overrides.removedDevices ?? {};
  const adoptedOv = overrides.adoptedDevices ?? {};

  // Every circuit-keyed map is re-addressed onto the circuits actually here
  // before it is read. `circuitId` embeds a board name the run re-decides each
  // run, so his drop, his reserve and his feed point are stored under an id the
  // next run may not publish — and a lost feed point silently re-anchors a
  // cable to the board he moved it off. `resolveCircuitEdits` refuses anything
  // ambiguous rather than picking.
  // The runs HE created, minus any the estimation has since published itself.
  // Resolved before anything reads a circuit-keyed map, because a created run
  // is a live circuit for every purpose below — including his own drops, cable
  // type and feed point on it.
  const addedCircuits = liveAddedCircuits({
    addedCircuits: overrides.addedCircuits,
    baseCircuitIds: Object.keys(base.byCircuit),
  });
  const addedCircuitIds = Object.keys(addedCircuits);
  const liveCircuitIds = [...Object.keys(base.byCircuit), ...addedCircuitIds];
  const boardNames = base.sources.map((source) => source.name);
  // App circuits are addressable too, so his drops and cable type on one survive a re-derive.
  const potentialAppIds = [
    ...base.sources.map((source) => source.label),
    ...Object.keys(overrides.addedSources ?? {}),
  ].flatMap((label) => [
    ...CATEGORY_ORDER.map((category) => appCircuitId(label, category)),
    feederCircuitId(label),
  ]);
  const onLive = <T>(edits: Record<string, T> | undefined) =>
    resolveCircuitEdits({
      edits,
      liveCircuitIds: [...liveCircuitIds, ...potentialAppIds],
      boardNames,
    });
  const strandedOv = onLive(overrides.strandedByCircuit);
  const feedPointOv = onLive(overrides.feedPointByCircuit);
  const cableTypeOv = onLive(overrides.cableTypeByCircuit);
  const legTypeOv = onLive(overrides.cableTypeByLeg);
  const routedOv = onLive(overrides.routedByCircuit);

  // Removed-device markers computed from the ORIGINAL geometry (last waypoint of
  // each dropped path, in base/PDF-point space) so the plan can highlight them.
  const removedDeviceMarkers: RemovedDeviceMarker[] = [];
  Object.entries(base.byCircuit).forEach(([cid, state]) => {
    state.paths.forEach((p) => {
      if (!readDeviceEdit({ edits: removedDevOv, address: p })) return;
      const last = p.waypoints[p.waypoints.length - 1];
      if (last) {
        removedDeviceMarkers.push({
          x: last.x,
          y: last.y,
          destinationId: p.destinationId,
          // Both: the address is what the destination lists are indexed by,
          // the key is what a removal group remembers him striking.
          deviceKey: deviceKeyOf(p),
          circuitId: cid,
        });
      }
    });
  });

  // 1. Source markers with overrides applied (hidden sources dropped), then the
  //    ones the estimator added by hand — a moved added source is still moved.
  const added = overrides.addedSources ?? {};
  const sources: CableSourceMarker[] = base.sources
    .filter((s) => !removedOv[s.label])
    .map((s) => {
      const ov = sourceOv[s.label];
      return ov ? { ...s, x: ov.x, y: ov.y } : { ...s };
    });
  Object.entries(added).forEach(([label, source]) => {
    if (removedOv[label]) return;
    const ov = sourceOv[label];
    sources.push({
      x: ov?.x ?? source.x,
      y: ov?.y ?? source.y,
      label,
      name: source.name,
    });
  });
  const newSourceByLabel: Record<string, RouteWaypoint> = {};
  sources.forEach((s) => {
    newSourceByLabel[s.label] = { x: s.x, y: s.y };
  });

  // Every point on this plan that IS a board, whether or not it still is one:
  // struck boards and the positions boards were moved off are both in here. It
  // answers one question — "is this cable starting on a board?" — and a cable
  // left standing on a board the run no longer feeds from is the bug it exists
  // to catch, which is exactly the case a struck board makes.
  const boardPoints: { label: string; at: RouteWaypoint }[] = [];
  const boardPoint = (label: string, at: RouteWaypoint) => {
    if (boardPoints.some((one) => one.label === label && samePoint(one.at, at)))
      return;
    boardPoints.push({ label, at });
  };
  base.sources.forEach((s) => boardPoint(s.label, { x: s.x, y: s.y }));
  Object.entries(added).forEach(([label, source]) =>
    boardPoint(label, { x: source.x, y: source.y }),
  );
  Object.entries(sourceOv).forEach(([label, at]) =>
    boardPoint(label, { x: at.x, y: at.y }),
  );

  /** The board a cable visibly STANDS ON, if it stands on one at all. */
  const boardAt = (at: RouteWaypoint): string | null =>
    boardPoints.find((one) => samePoint(one.at, at))?.label ?? null;

  // Stable board->circuit mapping computed from ORIGINAL geometry. This is the
  // DERIVED nearest-marker board, not the estimator's `feedPointByCircuit` — it
  // is only what the run's own geometry implies, and the override outranks it.
  const boardByCircuit: Record<string, string | null> = {};
  const rgbByCircuit: Record<string, [number, number, number] | undefined> = {};
  const runSources = base.sources.filter((source) => source.usedByRun !== false);
  Object.entries(base.byCircuit).forEach(([cid, state]) => {
    boardByCircuit[cid] =
      base.runBoardByCircuit?.[cid] ?? boardLabelForPaths(state.paths, runSources);
    rgbByCircuit[cid] = state.paths[0]?.rgb;
  });
  // A created run has no geometry to read a board off, so it carries the one he
  // named — and a colour of its own, because the first device moved onto it
  // would otherwise keep the colour of the run it LEFT, and he navigates
  // eighty runs on this sheet by colour.
  addedCircuitIds.forEach((cid) => {
    boardByCircuit[cid] = addedCircuits[cid].sourceLabel;
    rgbByCircuit[cid] = mintRunColour({ circuitId: cid });
  });

  // Every device's category: the agent's label for its type, else its circuit's system.
  const typeNameOf = (p: CircuitPathResult): string | undefined =>
    p.elementId ? base.deviceTypeByElementId?.[p.elementId]?.name : undefined;
  const categoryOf = (p: CircuitPathResult, cid: string): ElementCategory =>
    (p.elementId ? base.deviceTypeByElementId?.[p.elementId]?.category : undefined) ??
    categoryOfCableSystem(systemByCircuit[cid]) ??
    "other";
  const unbilled = new Set(overrides.unbilledCategories ?? []);
  const isUnbilled = (p: CircuitPathResult, cid: string): boolean => {
    const category = categoryOf(p, cid);
    return category !== "other" && unbilled.has(category);
  };
  const isLiveDevice = (p: CircuitPathResult, cid: string): boolean =>
    !readDeviceEdit({ edits: removedDevOv, address: p }) && !isUnbilled(p, cid);

  const categoryByCircuit: Record<string, DeviceCategory | null> = {};
  Object.entries(base.byCircuit).forEach(([cid, state]) => {
    const counts = new Map<DeviceCategory, number>();
    state.paths.forEach((p) => {
      const category = categoryOf(p, cid);
      if (category === "other" || !isLiveDevice(p, cid)) return;
      counts.set(category, (counts.get(category) ?? 0) + 1);
    });
    categoryByCircuit[cid] =
      [...counts.entries()].sort(
        ([a, countA], [b, countB]) =>
          countB - countA || CATEGORY_ORDER.indexOf(a) - CATEGORY_ORDER.indexOf(b),
      )[0]?.[0] ?? null;
  });
  addedCircuitIds.forEach((cid) => {
    categoryByCircuit[cid] = categoryOfCableSystem(addedCircuits[cid].system) ?? null;
  });

  const feedsByBoard = resolveBoardFeeds({
    sources,
    stored: overrides.boardFeeds ?? {},
  });
  const feeds: Record<string, BoardFeed> = Object.fromEntries(
    Object.entries(feedsByBoard).map(([label, resolved]) => [label, resolved.feed]),
  );
  const boardFeedsCategory = (label: string, category: DeviceCategory): boolean =>
    feedsDevice({ feed: feeds[label], category });
  const liveAreas = (overrides.areaFeeds ?? []).filter((area) => newSourceByLabel[area.sourceLabel]);
  // A board he drew zones for reaches only inside them: SIBE's area is where SIBE's cable goes.
  const reaches = (label: string, point: RouteWaypoint): boolean => {
    const own = liveAreas.filter((area) => area.sourceLabel === label);
    return own.length === 0 || own.some((area) => isInsideFeedArea({ point, area }));
  };

  // Where each run's LIVE devices are, which is the only thing an area feed
  // needs to know about it. Only the devices of the run's own category count:
  // the others are split off to the board that feeds them anyway.
  const devicesByCircuit: Record<string, RouteWaypoint[]> = {};
  Object.entries(base.byCircuit).forEach(([cid, state]) => {
    const category = categoryByCircuit[cid];
    devicesByCircuit[cid] = state.paths
      .filter((p) => isLiveDevice(p, cid))
      .filter((p) => !category || categoryOf(p, cid) === category)
      .map((p) => p.waypoints[p.waypoints.length - 1])
      .filter((at): at is RouteWaypoint => Boolean(at));
  });
  const areaBoardByCircuit = governedByFeedAreas({
    devicesByCircuit,
    systemByCircuit,
    categoryByCircuit,
    feedsCategory: boardFeedsCategory,
    areas: overrides.areaFeeds ?? [],
    isSourceLive: (label) => Boolean(newSourceByLabel[label]),
  });

  // Which board a circuit actually feeds from. Step 2 anchors that circuit's own
  // paths on it and step 3 anchors a path boxed ONTO it on the same answer, so a
  // reassigned cable can never start somewhere the run itself does not start.
  //
  // Three answers, strongest first, and the order is "the more specific
  // instruction wins" — the same precedence the whole pipeline runs on:
  //   1. the board he named for THIS run,
  //   2. the board an area of the plan says this region feeds from,
  //   3. the board the run's own geometry started at.
  // A run he re-points by hand inside an area therefore keeps his answer, and
  // that is also what a straddling run's question writes.
  const isLive = (label: string) => Boolean(newSourceByLabel[label]);
  const mainBoard = resolveMainBoard({
    answer: overrides.mainBoard,
    suggestion: base.mainBoardSuggestion ?? null,
    isLive,
  });
  const mainLabel = mainBoard.kind === "none" ? null : mainBoard.label;
  const roleAnswers = overrides.boardRoleAnswers ?? {};
  const circuitBoardByCircuit: Record<string, CircuitBoard> = {};
  liveCircuitIds.forEach((cid) => {
    circuitBoardByCircuit[cid] = resolveCircuitBoard({
      feedPoint: feedPointOv[cid],
      zoneBoard: areaBoardByCircuit[cid],
      runBoard: boardByCircuit[cid] ?? null,
      mainLabel,
      isLive,
      isStruck: (label) => Boolean(removedOv[label]),
      roleOf: (label) => roleAnswers[label],
    });
  });
  const firstDeviceOf = (cid: string, category: DeviceCategory): RouteWaypoint | null => {
    const path = base.byCircuit[cid]?.paths.find(
      (p) => isLiveDevice(p, cid) && categoryOf(p, cid) === category,
    );
    return path?.waypoints[path.waypoints.length - 1] ?? null;
  };
  const unfedCategories: Partial<Record<DeviceCategory, string[]>> = {};
  const markUnfed = (category: DeviceCategory, deviceKey: string) => {
    unfedCategories[category] = [...(unfedCategories[category] ?? []), deviceKey];
  };
  const feederFor = (category: DeviceCategory, point: RouteWaypoint | null, typeName?: string) =>
    pickFeeder({
      category,
      typeName,
      point,
      feeds,
      positions: newSourceByLabel,
      mainLabel,
      zones: liveAreas,
      reaches,
    });

  // 2a. A board feeds only its categories: a run of another category goes to a board that does.
  Object.keys(base.byCircuit).forEach((cid) => {
    const resolved = circuitBoardByCircuit[cid];
    const category = categoryByCircuit[cid];
    if (!resolved || resolved.by === "yours" || !category) return;
    const first = firstDeviceOf(cid, category);
    const board = resolved.label;
    if (
      board &&
      newSourceByLabel[board] &&
      boardFeedsCategory(board, category) &&
      (!first || reaches(board, first))
    )
      return;
    const picked = feederFor(category, first);
    if (picked) circuitBoardByCircuit[cid] = { label: picked.label, by: "category", reason: picked.reason };
  });

  const boardLabelOf = (cid: string): string | null =>
    circuitBoardByCircuit[cid]?.label ?? null;

  // 2b. A device its run's board does not feed moves onto an app circuit of a board that does.
  //     Never stored: his own moves are applied after these and win.
  const appCircuits: Record<string, AppCircuit> = {};
  const autoGroupOv: Record<string, string> = {};
  const unfedDevices: UnfedDevice[] = [];
  const unfedKeys = new Set<string>();
  const addedSet = new Set(addedCircuitIds);
  Object.entries(base.byCircuit).forEach(([cid, state]) => {
    if (addedSet.has(cid) || circuitBoardByCircuit[cid]?.by === "yours") return;
    const board = boardLabelOf(cid);
    if (board && removedOv[board]) return;
    state.paths.forEach((p) => {
      if (!isLiveDevice(p, cid)) return;
      const key = deviceKeyOf(p);
      if (groupOv[key] || groupOv[p.destinationId]) return;
      const category = categoryOf(p, cid);
      if (category === "other") return;
      const typeName = typeNameOf(p);
      const at = p.waypoints[p.waypoints.length - 1] ?? null;
      if (board && feedsDevice({ feed: feeds[board], category, typeName }) && (!at || reaches(board, at)))
        return;
      const picked = feederFor(category, at, typeName);
      if (!picked) {
        markUnfed(category, key);
        if (p.elementId && at) {
          unfedKeys.add(key);
          unfedDevices.push({
            elementId: p.elementId,
            destinationId: p.destinationId,
            name: typeName ?? p.elementId,
            category,
            position: at,
          });
        }
        return;
      }
      if (picked.label === board) return;
      const target = appCircuitId(picked.label, category);
      appCircuits[target] = { sourceLabel: picked.label, category };
      autoGroupOv[key] = target;
    });
  });
  const registerAppCircuit = (id: string, app: AppCircuit, reason: PickFeederReason) => {
    appCircuits[id] = app;
    boardByCircuit[id] = app.sourceLabel;
    rgbByCircuit[id] = mintRunColour({ circuitId: id });
    categoryByCircuit[id] = app.category;
    circuitBoardByCircuit[id] = { label: app.sourceLabel, by: "category", reason };
  };
  Object.entries(appCircuits).forEach(([id, app]) => registerAppCircuit(id, app, "onlyFeeder"));
  // A device he moved onto an app circuit keeps it alive while its board still feeds that category.
  Object.values(groupOv).forEach((target) => {
    if (appCircuits[target]) return;
    const match = CATEGORY_ORDER.flatMap((category) =>
      Object.keys(newSourceByLabel)
        .filter((label) => appCircuitId(label, category) === target)
        .map((label) => ({ label, category })),
    )[0];
    if (match && boardFeedsCategory(match.label, match.category)) {
      registerAppCircuit(target, { sourceLabel: match.label, category: match.category }, "onlyFeeder");
    }
  });

  // A board that feeds power is fed itself: from the board he named, else the main board.
  // Never the main board's own feeder, and never a board fed from another floor.
  const feederCircuits: Record<string, FeederCircuit> = {};
  const offFloor = overrides.feederOffFloor ?? {};
  sources.forEach((source) => {
    const toLabel = source.label;
    if (toLabel === mainLabel || offFloor[toLabel]) return;
    const feedsPower = (feeds[toLabel]?.categories ?? []).some((category) =>
      POWER_BOARD_CATEGORIES.includes(category),
    );
    if (!feedsPower) return;
    const id = feederCircuitId(toLabel);
    const named = feedPointOv[id];
    const fromLabel = named && named !== toLabel && isLive(named) ? named : mainLabel;
    if (!fromLabel) return;
    feederCircuits[id] = { fromLabel, toLabel };
    boardByCircuit[id] = fromLabel;
    rgbByCircuit[id] = FEEDER_RGB;
    categoryByCircuit[id] = null;
    circuitBoardByCircuit[id] =
      fromLabel === named
        ? { label: fromLabel, by: "yours" }
        : { label: fromLabel, by: "main", reason: "noRunBoard" };
  });

  const feedingBoardByCircuit: Record<string, string | null> = {};
  [...liveCircuitIds, ...Object.keys(appCircuits), ...Object.keys(feederCircuits)].forEach((cid) => {
    feedingBoardByCircuit[cid] = boardLabelOf(cid);
  });

  // 2. Clone working paths per circuit and apply route + source overrides.
  // Circuits carrying a cable this code invented (a tap, or a re-anchor onto the main board) rather than the router.
  const approximated = new Set<string>();
  const circuitPaths: Record<string, CircuitPathResult[]> = {};
  let orphanedDeviceCount = 0;
  const orphanedLabels = new Set<string>();
  const orphanedCircuitIds = new Set<string>();
  Object.entries(base.byCircuit).forEach(([cid, state]) => {
    const derivedLabel = boardByCircuit[cid];
    // Resolved before the struck-board check and before the re-anchor below,
    // because it IS the answer to "which board feeds this run": a run the
    // estimator re-pointed at a live board is not orphaned by the struck one it
    // came from, and the re-anchor has to see the board he chose. A label no
    // longer on the plan (struck, or stale in storage) is no answer, so it
    // degrades to the derived board instead of unfeeding the run.
    const boardLabel = boardLabelOf(cid);
    const feedAt = boardLabel ? newSourceByLabel[boardLabel] : undefined;
    const reanchorTo =
      boardLabel && (boardLabel !== derivedLabel || sourceOv[boardLabel])
        ? newSourceByLabel[boardLabel]
        : null;
    // A board that was never a feed point cannot still be feeding cable. Its
    // circuits leave with it — not drawn, not counted — the same way a removed
    // device does. Hiding only the marker left every route hanging off a point
    // that is no longer on the plan, still in the total.
    if (boardLabel && removedOv[boardLabel]) {
      circuitPaths[cid] = [];
      orphanedCircuitIds.add(cid);
      orphanedDeviceCount += state.paths.filter(
        (p) => !readDeviceEdit({ edits: removedDevOv, address: p }),
      ).length;
      orphanedLabels.add(boardLabel);
      return;
    }
    // Drop removed devices entirely — not drawn, not counted. A device no board reaches gets no cable either.
    circuitPaths[cid] = state.paths
      .filter((p) => isLiveDevice(p, cid) && !unfedKeys.has(deviceKeyOf(p)))
      .map((p) => {
        const path = clonePath(p);
        // Keyed by destinationId, which is minted from the base geometry and
        // travels with the path — so a hand-drawn route still finds its own
        // device after step 3 has reassigned it to another circuit. Keyed by
        // `${circuitId}::${destIndex}` it did not: the drag read the position
        // from the DERIVED geometry and this lookup used the BASE circuit, so
        // one reassignment was enough to redraw a different device's cable.
        const drawn = readDeviceEdit({ edits: routeOv, address: path });
        if (drawn) {
          path.waypoints = drawn.map((w) => ({ ...w }));
        }
        // A path with no second waypoint is a device the reshape has cut off:
        // it has no cable and no shape to re-anchor, and re-anchoring it would
        // replace the device's own position with the board's and move the
        // symbol off the thing it stands for.
        //
        // A STORED path is re-anchored on where it actually begins, not on
        // which board this run was derived on. The two triggers above are both
        // about the run: he re-pointed it, or its board moved under it. Neither
        // fires when he points a run back at the board it started on — and the
        // router's answer, written while another board was feeding it, begins
        // at THAT board. *"i changed the source to PA, and back to GHV, but it
        // didn't remove the previous route from PA to A3. It is hanging. Even
        // when I delete PA, it still shows the wiring there."* The check is
        // "does this cable stand on a board that is not the one feeding it",
        // which is why `boardPoints` remembers struck boards and the positions
        // boards were moved off.
        const standingOn = drawn ? boardAt(path.waypoints[0]) : null;
        const anchor =
          reanchorTo ??
          (feedAt && standingOn && standingOn !== boardLabel ? feedAt : null);
        if (anchor && path.waypoints.length > 1) {
          path.waypoints = manhattanReanchor(anchor, path.waypoints);
          if (circuitBoardByCircuit[cid]?.by === "main") approximated.add(cid);
        }
        path.coordinateExtents = extentsOf(path.waypoints);
        return path;
      });
  });

  // A run he created starts EMPTY, and it has to exist all the same: a circuit
  // with no entry here takes no device, is measured nowhere, and shows a row
  // over a run the plan does not have. It is struck off with its board like any
  // published run — its devices are still drawn and still have to go somewhere.
  addedCircuitIds.forEach((cid) => {
    circuitPaths[cid] = [];
    const boardLabel = boardLabelOf(cid);
    if (boardLabel && removedOv[boardLabel]) {
      orphanedCircuitIds.add(cid);
      orphanedLabels.add(boardLabel);
    }
  });

  Object.keys(appCircuits).forEach((id) => {
    circuitPaths[id] = [];
  });
  const lostDevices = new Set<string>();

  // 3. Group reassignment — move a destination path to another circuit, recolor,
  //    and re-anchor it onto the board that circuit actually feeds from. The
  //    app's category moves first, so his own moves overwrite them.
  Object.entries({ ...autoGroupOv, ...groupOv }).forEach(([deviceKey, toCircuitId]) => {
    let moved: CircuitPathResult | null = null;
    let fromCircuitId: string | null = null;
    for (const cid of Object.keys(circuitPaths)) {
      const idx = circuitPaths[cid].findIndex(
        (p) => deviceKeyOf(p) === deviceKey || p.destinationId === deviceKey,
      );
      if (idx >= 0) {
        moved = circuitPaths[cid].splice(idx, 1)[0];
        fromCircuitId = cid;
        break;
      }
    }
    if (!moved || !fromCircuitId) return;
    if (appCircuits[fromCircuitId] === undefined) lostDevices.add(fromCircuitId);
    if (!circuitPaths[toCircuitId]) circuitPaths[toCircuitId] = [];
    moved.routeId = toCircuitId;
    moved.rgb = rgbByCircuit[toCircuitId] ?? moved.rgb;
    // Re-parenting and recolouring alone left the cable physically starting at
    // the board it LEFT: the wrong shape on the plan, and the wrong metres on
    // BOTH circuits. Only waypoint 0 moves, so a route the estimator drew by
    // hand keeps its shape from the second waypoint on — his drawing is the
    // more specific instruction, and the same trade manhattanReanchor already
    // makes for a board that moved under a hand-drawn route.
    const toLabel = boardLabelOf(toCircuitId);
    const anchor =
      toLabel && toLabel !== boardLabelOf(fromCircuitId)
        ? newSourceByLabel[toLabel]
        : undefined;
    // A device joining a run taps into the cable that is already there, at the
    // nearest point on it — that is how it is actually pulled, and it is what
    // "continue the existing route" means. Running it back to the board instead
    // drew a parallel cable to the same place for every device added, and made
    // the run's metres count one trunk per device.
    //
    // A hand-drawn route is the more specific instruction and keeps its shape;
    // it is only re-anchored, as before.
    const device = moved.waypoints[moved.waypoints.length - 1];
    const tap = readDeviceEdit({ edits: routeOv, address: moved })
      ? null
      : nearestPointOnPaths(circuitPaths[toCircuitId], device);
    if (tap) {
      moved.waypoints = manhattanSpur(tap, device);
      moved.coordinateExtents = extentsOf(moved.waypoints);
      approximated.add(toCircuitId);
    } else if (anchor) {
      // Nothing drawn on the target yet — start it at the board it feeds from.
      // No anchor either means the run has no board on the plan, and leaving
      // the cable where it is beats guessing one for it.
      moved.waypoints = manhattanReanchor(anchor, moved.waypoints);
      moved.coordinateExtents = extentsOf(moved.waypoints);
    }
    circuitPaths[toCircuitId].push(moved);
  });

  // 3b. Adoption — a device the run put on NO circuit, given one by hand. The
  //     only override that MINTS a path: there is nothing to re-parent, because
  //     a device without a circuit has no destination at all.
  //
  //     After the reassignment loop on purpose, so an adopted device can tap a
  //     cable that was itself just moved onto this run.
  Object.entries(adoptedOv).forEach(([elementId, adopted]) => {
    const destinationId = adoptedDestinationId(elementId);
    // The bin on its row writes `removedDevices` like any other device's; the
    // path is minted here, after step 2 filtered those, so it has to be checked
    // again or an adopted device could be added but never taken back out.
    if (
      readDeviceEdit({
        edits: removedDevOv,
        address: { elementId, destinationId },
      })
    )
      return;
    const paths = circuitPaths[adopted.circuitId];
    if (!paths) return;
    const position = { x: adopted.position.x, y: adopted.position.y };
    // What the ROUTER answered for this device, or what he drew by hand, beats
    // the spur — and it has to be read here, because this branch MINTS the path
    // rather than editing one step 2 already saw. Without it an adopted device
    // was re-spurred on every derive and the router's answer was thrown away
    // the moment it arrived: the metres moved, the cable did not.
    const drawn = readDeviceEdit({
      edits: routeOv,
      address: { elementId, destinationId },
    });
    const board = boardLabelOf(adopted.circuitId);
    const anchor = board ? newSourceByLabel[board] : undefined;
    const tap = drawn ? null : nearestPointOnPaths(paths, position);
    // A spur from the board, never `manhattanReanchor`: that one replaces
    // waypoint 0 and keeps the REST, and a one-waypoint list has no rest — it
    // would return the board alone and drop the device off its own cable.
    const waypoints = drawn
      ? drawn
      : tap
        ? manhattanSpur(tap, position)
        : anchor
          ? manhattanSpur(anchor, position)
          : null;
    // Nothing drawn on the run and no board on the plan: no cable at all beats
    // a guessed one, and the device stays where the run left it.
    if (!waypoints) return;
    if (!drawn) approximated.add(adopted.circuitId);
    paths.push({
      routeId: adopted.circuitId,
      destinationId,
      // It is this element's cable, so it says so — then every device override
      // keys on the same durable id whether the run drew the path or we did.
      elementId,
      destinationName: adopted.name,
      waypoints,
      trayMeters: 0,
      totalMeters: 0,
      reachable: true,
      // The run's colour, not just the colour of what is already drawn on it:
      // the first device adopted onto a run he created finds nothing there to
      // take a colour from, and an uncoloured cable reads as a different thing.
      rgb: paths[0]?.rgb ?? rgbByCircuit[adopted.circuitId],
      coordinateExtents: extentsOf(waypoints),
    });
  });

  // 3c-pre. Each feeder is one cable from its upstream board to the board it feeds.
  Object.entries(feederCircuits).forEach(([id, feeder]) => {
    const from = newSourceByLabel[feeder.fromLabel];
    const to = newSourceByLabel[feeder.toLabel];
    if (!from || !to) return;
    const destinationId = feederDestinationId(feeder.toLabel);
    const elementId = feederElementId(feeder.toLabel);
    const drawn = readDeviceEdit({ edits: routeOv, address: { elementId, destinationId } });
    const waypoints = drawn ?? manhattanSpur(from, to);
    if (!drawn) approximated.add(id);
    circuitPaths[id] = [
      {
        routeId: id,
        destinationId,
        elementId,
        destinationName: sources.find((source) => source.label === feeder.toLabel)?.name ?? feeder.toLabel,
        waypoints,
        trayMeters: 0,
        totalMeters: 0,
        reachable: true,
        rgb: FEEDER_RGB,
        coordinateExtents: extentsOf(waypoints),
        isFeeder: true,
      },
    ];
  });

  // Every cable on a run HE created was drawn by this code: the estimation never
  // routed this circuit, so each device on it arrived by a tap or a re-anchor.
  // It carries the same mark a tapped device raises, and the mark clears the
  // moment the router answers for it — `fresh` below settles that.
  addedCircuitIds.forEach((cid) => {
    if (circuitPaths[cid]?.length) approximated.add(cid);
  });
  // The app's circuits are drawn by this code too, and a run that lost devices to them needs its tree again.
  Object.keys(appCircuits).forEach((id) => {
    if (circuitPaths[id]?.length) approximated.add(id);
  });
  lostDevices.forEach((cid) => {
    if (circuitPaths[cid]?.length) approximated.add(cid);
  });

  // 3c. A guessed cable takes the router's answer when there is one for exactly
  //     these inputs; otherwise it is asked for. Hand-drawn devices are never replaced.
  // A feeder is no run of the estimation's, so it bills at the plan's own Zuschlag — one per plan in practice.
  const planSurchargePct =
    Object.values(base.defaults ?? {}).find((facts) => facts.noTraySurchargePct !== undefined)
      ?.noTraySurchargePct ?? 0;
  const surchargeOf = (cid: string): number =>
    base.defaults?.[cid]?.noTraySurchargePct ?? (feederCircuits[cid] ? planSurchargePct : 0);
  const routeRequests: RouteRequest[] = [];
  const routedNow: Record<string, RouteAnswer> = {};
  const toRoute = routing.routeAll
    ? new Set([...approximated, ...Object.keys(circuitPaths)])
    : approximated;
  toRoute.forEach((cid) => {
    const paths = circuitPaths[cid] ?? [];
    const board = boardLabelOf(cid);
    const source = (board ? newSourceByLabel[board] : undefined) ?? paths[0]?.waypoints[0];
    const routable = paths.filter(
      (path) => path.elementId && !readDeviceEdit({ edits: routeOv, address: path }),
    );
    const destinations = routeDestinations({
      paths: routable,
      deviceTypeByElementId: base.deviceTypeByElementId,
    });
    if (!source || destinations.length === 0) return;
    const noTraySurchargePct = surchargeOf(cid);
    const key = routeInputKey({
      circuitId: cid,
      source,
      destinations,
      noTraySurchargePct,
    });
    const answer = routing.cache[key];
    if (!answer) {
      routeRequests.push({
        circuitId: cid,
        noTraySurchargePct,
        circuit: { ref: key, trayType: "", source, destinations },
      });
      return;
    }
    const byElement = new Map(answer.paths.map((path) => [path.elementId, path.waypoints]));
    circuitPaths[cid] = paths.map((path) => {
      const waypoints =
        path.elementId && routable.includes(path) ? byElement.get(path.elementId) : undefined;
      if (!waypoints?.length) return path;
      const copied = waypoints.map((w) => ({ ...w }));
      return { ...path, waypoints: copied, coordinateExtents: extentsOf(copied) };
    });
    routedNow[cid] = answer;
    approximated.delete(cid);
  });

  // 4. Assemble geometry + derive lengths.
  //    measured = routed length (geometry × scale);
  //    total    = billed + vertical, the vertical walked from his heights.
  const byCircuit: Record<string, CircuitPathsState> = {};
  const lengthsByCircuit: Record<string, number> = {};
  const measuredByCircuit: Record<string, number> = {};
  const billedByCircuit: Record<string, number> = {};
  const staleBilledCircuits: string[] = [];
  const circuitsNeedingRoute: string[] = [];
  // Holes through walls, as the ROUTER answered. Not derivable here: the client
  // has no wall model and no business having one — going round or through is the
  // router's decision, and this is only what it decided.
  const wallCrossingsByCircuit: Record<string, number> = {};
  const verticalByCircuit: Record<string, CircuitVertical> = {};
  const heightProfilesByCircuit: Record<string, DeviceHeightProfile[]> = {};
  const legsByCircuit: Record<string, CircuitLegMetres> = {};
  const heights = resolveHeights(overrides);
  const heightTrays = routing.trays ?? base.trays ?? [];
  const metresPerPoint = (imageScaleFactor || 1) / (scalePxPerM || DEFAULT_PX_PER_M);
  const allPaths: CircuitPathResult[] = [];
  const allStranded: StrandedEdge[] = [];
  let totalM = 0;
  Object.entries(circuitPaths).forEach(([cid, paths]) => {
    // Carried, never measured: `circuitMetres` below counts the PATHS, and a
    // length no cable runs over feeds nothing. It is on the drawing, dashed,
    // and in no total — which is the only honest place for it.
    const stranded = strandedOv[cid] ?? [];
    byCircuit[cid] = {
      paths,
      distributionBoardPosition: null,
      ...(stranded.length ? { strandedEdges: stranded } : {}),
    };
    allStranded.push(...stranded);
    const measured = circuitMetres({ paths, imageScaleFactor, scalePxPerM });
    // An orphaned circuit is fed from nowhere, so it has no height to climb from.
    const isOrphaned = orphanedLabels.size > 0 && orphanedCircuitIds.has(cid);
    const devices = paths.map((path) => ({
      at: path.waypoints[path.waypoints.length - 1] ?? { x: 0, y: 0 },
      level: installationLevelOf({
        deviceType: path.elementId ? base.deviceTypeByElementId?.[path.elementId] : undefined,
        name: path.destinationName,
      }),
    }));
    const profiles = isOrphaned
      ? []
      : paths.map((path, index) =>
          buildDeviceHeightProfile({
            destinationId: path.destinationId,
            waypoints: path.waypoints,
            trays: heightTrays,
            boardLabel: feedingBoardByCircuit[cid] ?? null,
            level: devices[index].level,
            heights,
            metresPerPoint,
            stops: heightStopsOf({ waypoints: path.waypoints, devices }),
            endM: path.isFeeder ? heights.boardM(feederCircuits[cid]?.toLabel) : undefined,
          }),
        );
    const vertical = circuitVerticalMetres(profiles);
    // The router's answer is what carries the surcharge; measuring alone
    // cannot, because the surcharge is per off-tray metre and only the router
    // knows which metres those are. An un-routed circuit falls back to measured,
    // which is what the panel has always shown.
    // Three sources, strongest first. HIS recalculate outranks the load-time
    // routing, which outranks measuring — and measuring is last because it is
    // the only one that cannot carry the no-tray surcharge, so it understates
    // any run that leaves a tray.
    const routed = routedOv[cid];
    const answered = routedNow[cid];
    const billed = answered
      ? answered.billedM
      : routed
        ? routed.billedM
        : (base.billedByCircuit?.[cid] ?? measured);
    const fresh = Boolean(answered) || routed?.fingerprint === fingerprintCircuit(paths);
    // Two different reasons to ask the router again, one mark on the run: the
    // stored figure no longer describes the drawing, or the drawing itself is
    // partly ours. A circuit the router has answered for AT this geometry is
    // neither, whatever it was a moment ago.
    // Only a run the router can be asked about; one with no addressable device would stay marked for good.
    const isRoutable = paths.some((path) => path.elementId);
    if (!fresh && isRoutable && (routed || approximated.has(cid))) {
      circuitsNeedingRoute.push(cid);
    }
    const crossings = answered ? answered.wallCrossings : routed?.wallCrossings;
    if (crossings) {
      wallCrossingsByCircuit[cid] = crossings;
    }
    if (routed && !fresh) {
      // Kept, not discarded — see `routedByCircuit`. The card marks the run so
      // he can ask the router again; it does not silently swap in a number that
      // is wrong by the whole Zuschlag.
      staleBilledCircuits.push(cid);
    }
    const total = billed + vertical.verticalM;
    measuredByCircuit[cid] = measured;
    billedByCircuit[cid] = billed;
    verticalByCircuit[cid] = vertical;
    heightProfilesByCircuit[cid] = profiles;
    legsByCircuit[cid] = isOrphaned
      ? {}
      : circuitLegs({
          paths,
          profiles,
          roleOf: (path) =>
            path.isFeeder
              ? null
              : switchingRoleOf({
              deviceType: path.elementId ? base.deviceTypeByElementId?.[path.elementId] : undefined,
              name: path.destinationName,
            }),
          metresPerPoint,
          noTraySurchargePct: surchargeOf(cid),
          billedM: billed,
        });
    lengthsByCircuit[cid] = total;
    totalM += total;
    allPaths.push(...paths);
  });

  return {
    geometry: {
      all: {
        paths: allPaths,
        distributionBoardPosition: null,
        ...(allStranded.length ? { strandedEdges: allStranded } : {}),
      },
      byCircuit,
      sources,
      // Carried through untouched. No override moves a tray or changes what the
      // run published about which run rides one — and dropping them here is
      // exactly what made the tray layer draw nothing at all: every unit test
      // passed because none of them went through this function.
      trays: base.trays,
      trayByCircuit: Object.keys(routedNow).length
        ? {
            ...base.trayByCircuit,
            ...Object.fromEntries(
              Object.entries(routedNow).map(([cid, answer]) => [
                cid,
                { onTrayM: answer.onTrayM, billedM: answer.billedM },
              ]),
            ),
          }
        : base.trayByCircuit,
      defaults: base.defaults ?? {},
      deviceTypeByElementId: base.deviceTypeByElementId,
    },
    lengthsByCircuit,
    measuredByCircuit,
    billedByCircuit,
    staleBilledCircuits,
    circuitsNeedingRoute,
    wallCrossingsByCircuit,
    verticalByCircuit,
    heightProfilesByCircuit,
    legsByCircuit,
    cableTypeByLegByCircuit: legTypeOv,
    feederCircuits,
    feedPointByCircuit: feedPointOv,
    cableTypeByCircuit: cableTypeOv,
    totalM,
    removedDeviceMarkers,
    feedingBoardByCircuit,
    circuitBoardByCircuit,
    mainBoard,
    routeRequests,
    appCircuits,
    categoryByCircuit,
    feedsByBoard,
    unfedCategories,
    unfedDevices,
    deviceCountsByBoard: countDevicesByBoard({ circuitPaths, feedingBoardByCircuit, categoryOfPath: (path, cid) => categoryOf(path, cid), typeNameOf }),
    orphanedDevices: {
      circuitCount: orphanedCircuitIds.size,
      deviceCount: orphanedDeviceCount,
      sourceLabels: [...orphanedLabels],
      circuitIds: [...orphanedCircuitIds],
    },
  };
}

/** Where an adopted device is, which run took it, and what to call it. */
export interface AdoptedDevice {
  circuitId: string;
  /** PDF points, like every other position in this app. */
  position: RouteWaypoint;
  name: string;
}

/** The destination id minted for an adopted device. */
export const adoptedDestinationId = (elementId: string): string =>
  `adopted:${elementId}`;

/** A route override key from before they were keyed by destinationId. */
const LEGACY_ROUTE_KEY = /^(.+)::(\d+)$/;

/**
 * Convert a stored override document to destinationId-keyed route overrides.
 * Idempotent, and a key it cannot read is kept rather than dropped — the
 * estimator has real hours saved under these.
 */
const migrateStoredAreaFeeds = (
  stored: CableEditOverrides,
): CableEditOverrides => {
  const areas = stored.areaFeeds as StoredFeedArea[] | undefined;
  if (!areas || areas.every((area) => "polygon" in area)) return stored;
  return { ...stored, areaFeeds: areas.map(normaliseFeedArea) };
};

// A boards step confirmed before the main board was asked has to be reopened, or the gate stays open without one.
const migrateToMainBoard = (stored: CableEditOverrides): CableEditOverrides => {
  if ((stored.schemaVersion ?? 1) >= EDIT_SCHEMA_VERSION) return stored;
  if (!stored.boardsConfirmed || stored.mainBoard) return stored;
  const { boardsConfirmed: _reopened, ...rest } = stored;
  return rest;
};

interface RetiredLengthEdits {
  dropsByCircuit?: unknown;
  reservePctByCircuit?: unknown;
}

// Drops and reserve were per-circuit guesses; the heights replaced them, so a stored one would only mislead.
const dropRetiredLengthEdits = (stored: CableEditOverrides & RetiredLengthEdits): CableEditOverrides => {
  if (stored.dropsByCircuit === undefined && stored.reservePctByCircuit === undefined) return stored;
  const { dropsByCircuit: _drops, reservePctByCircuit: _reserve, ...rest } = stored;
  return rest;
};

export function migrateStoredOverrides(
  input: CableEditOverrides,
): CableEditOverrides {
  const stored = dropRetiredLengthEdits(migrateToMainBoard(migrateStoredAreaFeeds(input)));
  const routeOv = stored.routeOverrides;
  if (!routeOv) return stored;

  const legacy: [string, RouteWaypoint[]][] = [];
  const migrated: Record<string, RouteWaypoint[]> = {};
  Object.entries(routeOv).forEach(([key, waypoints]) => {
    const match = key.match(LEGACY_ROUTE_KEY);
    if (match) legacy.push([`${match[1]}-${match[2]}`, waypoints]);
    else migrated[key] = waypoints;
  });
  if (legacy.length === 0) return stored;

  // A key already in destinationId form wins, so running this twice over a
  // document that holds both cannot undo the newer edit.
  legacy.forEach(([key, waypoints]) => {
    if (migrated[key] === undefined) migrated[key] = waypoints;
  });
  return { ...stored, routeOverrides: migrated };
}

/** True when any override field carries a real edit. */
export function hasAnyOverride(o: CableEditOverrides): boolean {
  const nonEmpty = (r?: Record<string, unknown>) =>
    r !== undefined && Object.keys(r).length > 0;
  return (
    nonEmpty(o.sourceOverrides) ||
    nonEmpty(o.routeOverrides) ||
    nonEmpty(o.groupOverrides) ||
    nonEmpty(o.removedDevices) ||
    nonEmpty(o.boardHeights) ||
    nonEmpty(o.installationHeights) ||
    nonEmpty(o.trayHeights) ||
    nonEmpty(o.removedSources) ||
    nonEmpty(o.sourceNames) ||
    nonEmpty(o.addedSources) ||
    nonEmpty(o.cableTypeByCircuit) ||
    (o.cableTypes ?? []).length > 0 ||
    nonEmpty(o.cableTypeByLeg) ||
    nonEmpty(o.feederOffFloor) ||
    nonEmpty(o.feedPointByCircuit) ||
    // An area is his work even before a single run has landed in it: it governs
    // every run the next estimation puts inside it, so a document holding only
    // areas is not an untouched document.
    (o.areaFeeds !== undefined && o.areaFeeds.length > 0) ||
    // Without this an adoption does not mark the takeoff edited, so `useDerived`
    // stays false and the header keeps printing the run's own total while the
    // list below it shows the device he just adopted.
    nonEmpty(o.adoptedDevices) ||
    // A run he created is his work before a single device is on it: it is a row
    // in the list and a circuit in the geometry, and a document holding one
    // reads as untouched without this — so the header keeps printing the run's
    // own total under a list that already shows his run.
    nonEmpty(o.addedCircuits) ||
    // A cable he drew that carries nobody is still work he did. Without this a
    // document holding nothing else reads as untouched, `useDerived` stays
    // false, and the drawn length is dropped on the next derive — which is the
    // same disappearance in a different place.
    nonEmpty(o.strandedByCircuit) ||
    o.mainBoard !== undefined ||
    nonEmpty(o.boardRoleAnswers) ||
    nonEmpty(o.boardFeeds) ||
    nonEmpty(o.circuitNames) ||
    nonEmpty(o.circuitKeys) ||
    (o.unbilledCategories ?? []).length > 0
  );
}

/** True when there is nothing worth storing — a confirmation alone is still worth storing. */
export const isEmptyDocument = (o: CableEditOverrides): boolean =>
  !hasAnyOverride(o) &&
  !o.boardsConfirmed &&
  !o.heightsConfirmed &&
  !o.trayHeightsConfirmed &&
  !o.wallsConfirmed &&
  (o.circuitBoardsChecked ?? []).length === 0;
